// Can Teamio text THIS PATIENT's number? — the single frontend implementation.
// Import this rather than adding another copy.
//
// ── THE RULE, AND WHERE IT COMES FROM ────────────────────────────────────────
// This mirrors the send endpoint EXACTLY:
//   teamio-serverless-api/main-service/task.js :: getPatientPhoneByPatNum
//     blank/absent                     -> NO_PHONE_NUMBER
//     fewer than 10 digits             -> INVALID_PHONE_FORMAT ("too short")
//     exactly 10 digits                -> OK, sent as +1XXXXXXXXXX
//     exactly 11 digits starting with 1-> OK, sent as +1XXXXXXXXXX
//     anything else (12+, 11 not 1...) -> INVALID_PHONE_FORMAT
// That handler is the authority: it re-reads the patient from the Open Dental mirror
// at send time and returns 400 on its own verdict. This module exists so the UI can
// say the same thing BEFORE a message is composed, not so it can decide anything new.
//
// ⛔ NEVER MAKE THIS STRICTER THAN THE BACKEND. A frontend rule that rejects more than
// the backend accepts does not prevent a bad send — it BLOCKS A GOOD ONE, silently, with
// no server-side error to alert on. That is not hypothetical here:
//
//   * 2026-06-01 — teamio-utility gated sends on google-libphonenumber's isValidNumber().
//     It false-negatived a real Miami 656-overlay number and blocked real patient texts.
//     The fix was to STOP gating on it (utility-utils.js resolveSmsNumber /
//     shouldAttemptSmsSend now require only that the number PARSES).
//   * CreateFollowup.vue used isValidNumberForRegion(x, 'US') for the same purpose and
//     inherited the same class of bug — measured 2026-09-09, it REJECTED the Edmonton
//     overlay 8255551234 that the backend sends to happily, ACCEPTED "555-123-4567 x123"
//     that the backend 400s, and THREW an unhandled exception on the literal text "none"
//     (which is what gets typed into an empty phone field). It now calls this module.
//
// So: digits and length only. No carrier lookup, no area-code table, no library.
//
// ── WHY THE VERDICT IS FINER-GRAINED THAN THE BACKEND'S ──────────────────────
// The backend returns ONE code, INVALID_PHONE_FORMAT, for every malformed shape, because
// all it has to decide is whether to send. The person reading this screen has a different
// job: FIXING THE NUMBER in Open Dental. "Invalid" does not tell them what to change, so
// `reason` splits that one code into the specific shapes a receptionist can act on —
// too few digits, one digit too many, an extension on the end, an international number,
// or text where a number should be. `code` stays backend-identical for parity; `reason`
// is purely for the sentence shown to a human. Saumil, 2026-09-09: "I would rather show
// those kind of useful messages to the end user than just saying you cannot send a text."
//
// ── THE DEMO MASK ────────────────────────────────────────────────────────────
// src/utils/phiMask.js replaces every digit with 'X' (MASK_STYLE 'literal'), so a masked
// patient's phone is "(XXX) XXX-XXXX" — zero digits. Judged as real data that reads as
// "no phone", which would grey out the Text button on every patient while recording a
// training video. Masked data is fake by construction and phiMaskInterceptor already
// BLOCKS /send-text-msg outright while the mask is on, so a data-quality verdict on it is
// meaningless: a mask placeholder is reported textable. Detected by shape (contains an X,
// contains no digit) so this module stays import-free.
//
// ── IMPORT-FREE ON PURPOSE ───────────────────────────────────────────────────
// cypress/node-tests/patient-phone-textable.test.js loads and executes THIS EXACT FILE
// rather than mirroring it. Keep it dependency-free (same contract as phiMask.js and
// logoUrlCheck.js).

/** Backend-parity verdict codes. OK and MASKED both mean "let them send". */
export const PHONE_OK = 'OK';
export const PHONE_MASKED = 'MASKED';
export const PHONE_MISSING = 'NO_PHONE_NUMBER';
export const PHONE_INVALID = 'INVALID_PHONE_FORMAT';

/**
 * The specific shape of the problem — what the human message is built from.
 * Every REASON except OK/MASKED/MISSING carries code PHONE_INVALID.
 */
export const REASON = {
  OK: 'OK',
  MASKED: 'MASKED',
  MISSING: 'MISSING',                              // nothing saved at all
  NOT_A_NUMBER: 'NOT_A_NUMBER',                    // "none", "n/a", "call mom"
  TOO_FEW_DIGITS: 'TOO_FEW_DIGITS',                // 1-9 digits
  ELEVEN_NOT_COUNTRY_CODE: 'ELEVEN_NOT_COUNTRY_CODE', // 11 digits not starting with 1
  TOO_MANY_DIGITS: 'TOO_MANY_DIGITS',              // 12+ digits, often an extension
  INTERNATIONAL: 'INTERNATIONAL',                  // written with a leading +
};

/**
 * Is this string the demo mask's placeholder rather than a real number?
 * "(XXX) XXX-XXXX" -> true. "none" -> false (no X). "5551234567" -> false (has digits).
 */
export function isMaskedPhone(raw) {
  if (!raw) return false;
  const s = String(raw);
  return /[Xx]/.test(s) && !/[0-9]/.test(s);
}

/**
 * The whole rule, in one place.
 *
 * @param {string|null|undefined} raw  patient.WirelessPhone, exactly as Open Dental holds it
 * @returns {{ok: boolean, code: string, reason: string, raw: string, digits: string, e164: string|null}}
 */
export function inspectPatientPhone(raw) {
  const str = raw === null || raw === undefined ? '' : String(raw);
  const trimmed = str.trim();

  const verdict = (ok, code, reason, digits, e164) => ({ ok, code, reason, raw: str, digits: digits || '', e164: e164 || null });

  if (trimmed === '') return verdict(false, PHONE_MISSING, REASON.MISSING, '');

  // A masked value carries no real digits — see "THE DEMO MASK" above.
  if (isMaskedPhone(str)) return verdict(true, PHONE_MASKED, REASON.MASKED, '');

  const digits = str.replace(/\D/g, '');

  // ── The two accepted shapes, byte-for-byte the backend's ──
  if (digits.length === 10) return verdict(true, PHONE_OK, REASON.OK, digits, '+1' + digits);
  if (digits.length === 11 && digits.charAt(0) === '1') return verdict(true, PHONE_OK, REASON.OK, digits, '+' + digits);

  // ── Everything below is a refusal; the only question is what to TELL them ──
  if (digits === '') return verdict(false, PHONE_INVALID, REASON.NOT_A_NUMBER, digits);

  // Ordered so the most actionable diagnosis wins. "Too few digits" beats "international"
  // because a short number is a typo whichever way it was written.
  if (digits.length < 10) return verdict(false, PHONE_INVALID, REASON.TOO_FEW_DIGITS, digits);
  if (trimmed.charAt(0) === '+') return verdict(false, PHONE_INVALID, REASON.INTERNATIONAL, digits);
  if (digits.length === 11) return verdict(false, PHONE_INVALID, REASON.ELEVEN_NOT_COUNTRY_CODE, digits);
  return verdict(false, PHONE_INVALID, REASON.TOO_MANY_DIGITS, digits);
}

/**
 * Would the send endpoint accept this patient's number?
 * Use for button enablement and pre-send guards.
 */
export function isTextablePhone(raw) {
  return inspectPatientPhone(raw).ok;
}

/**
 * What a dental receptionist should DO about it, or null when the number is fine.
 *
 * Every message answers three questions in order: what is saved, what is wrong with it,
 * and what to change. It quotes the number back because that is what let the operator at
 * Allay spot the typo on 2026-09-09, and it is the practice's own patient on the
 * practice's own screen (already masked here when the demo mask is on).
 *
 * Every message ends with "then reload this page" on purpose. The Open Dental mirror is
 * near-real-time (measured 2026-09-09: a corrected number reached the send path 44 seconds
 * later) but this screen keeps the copy it loaded, so "I fixed it and it's still blocked"
 * is a reload, not a bug.
 */
export function describePhoneProblem(raw) {
  const r = inspectPatientPhone(raw);
  if (r.ok) return null;

  const saved = 'Open Dental has "' + r.raw + '" saved as this patient\'s wireless phone number. ';
  const fix = ' Correct it in Open Dental, then reload this page.';
  const n = r.digits.length;

  switch (r.reason) {
    case REASON.MISSING:
      return 'This patient has no wireless phone number in Open Dental. Add their mobile number there, then reload this page.';

    case REASON.NOT_A_NUMBER:
      return saved + 'That is not a phone number, so there is nothing to text. Replace it with their 10-digit mobile number in Open Dental, then reload this page.';

    case REASON.TOO_FEW_DIGITS:
      return saved + 'That is only ' + n + (n === 1 ? ' digit' : ' digits') +
        ', and a mobile number needs 10: a 3-digit area code plus 7 more. It looks like some digits are missing.' + fix;

    case REASON.ELEVEN_NOT_COUNTRY_CODE:
      // The 2026-09-09 Allay case. This is the one worth explaining properly: 11 digits is
      // legitimate when the leading digit is the country code, so say why THIS one is not.
      return saved + 'That is 11 digits, and a mobile number needs 10. Eleven only works when the number starts with a 1 for the US country code, and this one starts with ' +
        r.digits.charAt(0) + ', so there is most likely one digit too many.' + fix;

    case REASON.TOO_MANY_DIGITS:
      return saved + 'That is ' + n + ' digits, ' + (n - 10) + ' more than a mobile number needs. If an extension is tacked on the end, remove it, because a text cannot be delivered to an extension.' + fix;

    case REASON.INTERNATIONAL:
      return saved + 'That looks like an international number, and this practice can only text US and Canadian numbers.' + fix;

    default:
      return saved + 'Teamio cannot text it, because it is not a valid 10-digit US or Canadian mobile number.' + fix;
  }
}

// ── A NUMBER THE AGENT TYPED (not read from Open Dental) ─────────────────────
// Starting a conversation with someone who is not a patient
// (docs/plans/2026-09-18-non-patient-sms-conversations-design.md) means the number comes from a
// text box instead of patient.WirelessPhone. The VERDICT is the same rule — one implementation,
// no second copy to drift — but two things differ, and both are why these live here rather than
// in a new module:
//
//  1. A mask placeholder is a REFUSAL here. inspectPatientPhone reports "(XXX) XXX-XXXX" as
//     textable on purpose, because a masked patient still has real digits underneath. A typed
//     mask string has nothing underneath it; it is just characters someone typed.
//  2. The message has to be about the BOX. describePhoneProblem's copy ends every sentence with
//     "correct it in Open Dental, then reload this page", which is meaningless advice for a
//     number that was never in Open Dental.
//
// ⛔ The thread key is the contract. A conversation is keyed on the E.164 that
// phoneFormat.normalizePhoneForRouting derives (see the layer), and the browser picks that key
// when it POSTs /create-conversation. For every input accepted here, `e164` MUST equal what the
// engine derives, or the agent types into one thread and the reply lands in another. Pinned by
// cypress/node-tests/typed-phone-entry.test.js. Being STRICTER than the engine is fine and
// intended (it refuses short, 11-not-1, and international numbers the engine would route);
// being DIFFERENT on an accepted number is the bug.

/**
 * Verdict for a number someone typed. Same rule as inspectPatientPhone, except a demo-mask
 * placeholder is refused rather than waved through.
 *
 * Guarantees, relied on by the caller: ok === true implies code === PHONE_OK implies e164 is a
 * routable E.164 string. A refusal always carries e164 === null, so there is no path where the
 * form thinks it succeeded but has nothing to POST.
 */
export function inspectTypedPhone(raw) {
  const r = inspectPatientPhone(raw);
  if (r.code === PHONE_MASKED) {
    return { ok: false, code: PHONE_INVALID, reason: REASON.NOT_A_NUMBER, raw: r.raw, digits: r.digits, e164: null };
  }
  return r;
}

/**
 * What to tell the agent about the number they just typed, or null when it is fine.
 *
 * Same three questions as describePhoneProblem — what you entered, what is wrong, what to change
 * — but the fix is always "edit the box", never "go fix the chart".
 */
export function describeTypedPhoneProblem(raw) {
  const r = inspectTypedPhone(raw);
  if (r.ok) return null;

  const typed = 'You entered "' + r.raw.trim() + '". ';
  const n = r.digits.length;

  switch (r.reason) {
    case REASON.MISSING:
      return 'Enter a mobile number to text, or pick a patient instead.';

    case REASON.NOT_A_NUMBER:
      return typed + 'That is not a phone number. Enter a 10-digit US or Canadian mobile number.';

    case REASON.TOO_FEW_DIGITS:
      return typed + 'That is only ' + n + (n === 1 ? ' digit' : ' digits') +
        ', and a mobile number needs 10: a 3-digit area code plus 7 more.';

    case REASON.ELEVEN_NOT_COUNTRY_CODE:
      return typed + 'That is 11 digits, and a mobile number needs 10. Eleven only works when the number starts with a 1 for the US country code, and this one starts with ' +
        r.digits.charAt(0) + ', so there is most likely one digit too many.';

    case REASON.TOO_MANY_DIGITS:
      return typed + 'That is ' + n + ' digits, ' + (n - 10) + ' more than a mobile number needs. If you added an extension, remove it — a text cannot be delivered to an extension.';

    case REASON.INTERNATIONAL:
      return typed + 'That looks like an international number, and this practice can only text US and Canadian numbers.';

    default:
      return typed + 'That is not a valid 10-digit US or Canadian mobile number.';
  }
}

/**
 * Is this typed number already one of the practice's patients?
 *
 * The roster is the one Vuex already holds (customer.patients), so this is a local scan, not a
 * lookup. Matching on the last 10 digits is the same comparison the backend makes
 * (WirelessPhoneNorm = SUBSTRING(?, -10)) so the two agree on who a number belongs to.
 *
 * ⛔ Both length guards are load-bearing. The backend carries an explicit warning that
 * SUBSTRING(?, -10) on a short value yields '' and therefore matches EVERY patient whose
 * WirelessPhone is empty — a stranger's number silently adopting some unrelated patient's chart.
 * Refusing to compare anything shorter than 10 digits, on either side, is what keeps that class
 * of false positive out of the frontend.
 *
 * @param {Array|null} roster  customer.patients rows ({ PatNum, Patient, WirelessPhone })
 * @param {string|null} e164   an accepted inspectTypedPhone().e164
 * @returns {object|null} the matching patient row, or null
 */
export function findPatientByPhone(roster, e164) {
  if (!Array.isArray(roster) || roster.length === 0) return null;

  const wanted = (e164 === null || e164 === undefined ? '' : String(e164)).replace(/\D/g, '');
  if (wanted.length < 10) return null;
  const wantedLast10 = wanted.slice(-10);

  for (let i = 0; i < roster.length; i += 1) {
    const p = roster[i];
    if (!p) continue;
    const digits = (p.WirelessPhone === null || p.WirelessPhone === undefined ? '' : String(p.WirelessPhone)).replace(/\D/g, '');
    if (digits.length < 10) continue;
    if (digits.slice(-10) === wantedLast10) return p;
  }
  return null;
}

/*
 * ── STARTING A CONVERSATION: WHO ARE WE ABOUT TO TEXT? ───────────────────────
 *
 * The "Start New Conversation" form has two controls that can each start a text: the Open Dental
 * patient picker, and a box for typing a number belonging to someone who is not a patient.
 * resolveNewConversationTarget is the ONE place that turns what is on that screen into a decision,
 * and the component has no other route to POST /create-conversation. That is deliberate: it makes
 * "the form refused" and "no message was sent" the same fact, provable in a node-test without a
 * browser.
 *
 * ⛔ TWO POPULATED CONTROLS IS A REFUSAL, NEVER A PREFERENCE. If the agent picks Jane and then
 * types a different number over the top, the screen shows two recipients and exactly one of them
 * is about to be texted. Picking either one for them sends a real message to a real person who was
 * not chosen, and the agent has no way to notice: the form closes and a thread appears, which is
 * what success looks like. Refusing costs one extra click and cannot text the wrong person.
 *
 * A typed number that turns out to BELONG to a patient resolves to the patient path instead of
 * opening a second, patient-less thread on the same phone. The engine would heal that eventually
 * (it re-resolves by phone on every message) but the agent would be staring at a nameless thread
 * next to the real one in the meantime.
 */

/**
 * What the New Conversation form is about to do.
 *
 * @param {object} [input]
 * @param {object|Array|null} [input.selectedPatient] the multiselect value (it hands back an array)
 * @param {string} [input.typedPhone]                 raw text from the phone box
 * @param {Array|null} [input.roster]                 customer.patients
 * @returns {{
 *   ok: boolean,
 *   kind: 'PATIENT'|'PHONE'|'EMPTY'|'CONFLICT'|'BAD_NUMBER',
 *   patient: object|null,
 *   e164: string|null,
 *   matchedFromTypedPhone: boolean,
 *   message: string|null
 * }}
 */
export function resolveNewConversationTarget(input) {
  const src = input || {};
  const raw = src.selectedPatient;
  const picked = Array.isArray(raw) ? (raw.length > 0 ? raw[0] : null) : (raw || null);
  const typed = (src.typedPhone === null || src.typedPhone === undefined) ? '' : String(src.typedPhone).trim();

  const target = (kind, patient, e164, matched, message) => ({
    ok: kind === 'PATIENT' || kind === 'PHONE',
    kind,
    patient: patient || null,
    e164: e164 || null,
    matchedFromTypedPhone: !!matched,
    message: message || null,
  });

  if (picked && typed !== '') {
    return target('CONFLICT', null, null, false,
      'Pick a patient or type a number — not both. Clear whichever one you do not want to text.');
  }
  if (!picked && typed === '') {
    return target('EMPTY', null, null, false,
      'Pick a patient, or type a mobile number to text someone who is not in Open Dental.');
  }
  if (picked) {
    return target('PATIENT', picked, null, false, null);
  }

  const verdict = inspectTypedPhone(typed);
  if (!verdict.ok) {
    return target('BAD_NUMBER', null, null, false, describeTypedPhoneProblem(typed));
  }

  const match = findPatientByPhone(src.roster, verdict.e164);
  if (match) {
    return target('PATIENT', match, verdict.e164, true, null);
  }
  return target('PHONE', null, verdict.e164, false, null);
}

/**
 * The `Users` array for POST /create-conversation, or null when there is nothing to send.
 *
 * ⛔ The PHONE branch carries WirelessPhone and NOTHING ELSE. createConversationInhouse writes
 * `patNum` / `patientName` / `friendlyName` only `if present`, so every key omitted here is a key
 * the Firestore thread doc will not carry. A fabricated name would be worse than none: the thread
 * self-heals into a real patient thread the moment that number is added to Open Dental, and a
 * placeholder name would survive that and sit on a real patient's conversation.
 *
 * @param {object|null} target a resolveNewConversationTarget() verdict
 * @returns {Array|null}
 */
export function newConversationUsersPayload(target) {
  if (!target || !target.ok) return null;
  if (target.kind === 'PATIENT') return [target.patient];
  return [{ WirelessPhone: target.e164 }];
}

/**
 * Does this conversation thread belong to someone who is NOT in Open Dental?
 *
 * Drives the "Not in Open Dental" badge in two places (the thread header and the room-row pill),
 * which is precisely why it lives here rather than inline in the component: two copies of this
 * rule would eventually disagree about the same thread, on the same screen.
 *
 * ⛔ THE ASYMMETRY MATTERS. A false NEGATIVE — no badge on a genuine non-patient thread — costs a
 * hint. A false POSITIVE tells staff that a real patient's messages are not reaching their chart
 * when they are, which is the kind of thing that gets acted on. So this answers `true` only when
 * there is NO evidence of a patient at all, and anything it cannot read is treated as evidence-free
 * input rather than as proof of a non-patient.
 *
 * Evidence of a patient, in order:
 *   1. `patNum` / `PatNum` — written by the layer (smsEngine.writeMessageToThread) on every message
 *      write, but only `if (patNum !== undefined && patNum !== null)`, and only since 2026-06-20.
 *   2. A meaningful, non-phone NAME. A doc that predates (1), or whose write raced, can carry a
 *      resolved patientName with no patNum — `!patNum` alone would badge a real patient there.
 *      This costs nothing in the other direction: decision 5 of the design leaves patientName and
 *      friendlyName UNSET on typed-number threads (see newConversationUsersPayload), so a true
 *      non-patient thread has neither, and its roomName is the formatted phone.
 *
 * Known limitation, accepted (design §5.4): this reads "the phone did not resolve to a patient at
 * the last write", so an Open Dental sync gap could mislabel a real patient's thread. The badge is
 * informational and gates nothing, and it self-corrects on the next message.
 *
 * @param {object|null} room a room/thread object as store/conversationsV2.js builds it
 * @returns {boolean} true when the badge should show
 */
export function threadIsNotInOpenDental(room) {
  if (!room || typeof room !== 'object' || Array.isArray(room)) return false;

  // PatNum 0 is not a patient: Open Dental numbers patients from 1, and 0 is what a bad coercion
  // of an absent value produces.
  const rawPatNum = room.patNum !== undefined && room.patNum !== null ? room.patNum : room.PatNum;
  if (rawPatNum !== undefined && rawPatNum !== null) {
    const n = Number(String(rawPatNum).trim());
    if (Number.isFinite(n) && n > 0) return false;
  }

  // Same two rejections the room-name resolution in store/conversationsV2.js already makes: the
  // 'Unknown Patient' placeholder (which cleanupUnknownPatients also keys on) and a "name" that is
  // really a phone number.
  const isRealName = (v) => {
    if (typeof v !== 'string') return false;
    const s = v.trim();
    if (!s) return false;
    if (s.toLowerCase() === 'unknown patient') return false;
    // "Is this string a phone number?" — punctuation-only characters plus enough digits. NOT the
    // store's `^\+?\d[\d\s().-]{5,}$`, which requires a leading DIGIT and so fails to recognise
    // "(585) 479-3418" — the exact spelling formatPhoneForDisplay() gives these threads.
    if (/^[+\d\s().\-x]+$/i.test(s) && s.replace(/\D/g, '').length >= 7) return false;
    return true;
  };
  if (isRealName(room.patientName) || isRealName(room.friendlyName)) return false;

  return true;
}
